import { useState, useCallback, useEffect, useMemo } from "react";
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import { type Address, formatUnits, maxUint160 } from "viem";
import {
  ADDRESSES,
  V2_ROUTER_ABI,
  V2_FACTORY_ABI,
  V2_PAIR_ABI,
} from "../config/contracts";
import {
  SMART_ROUTER_ABI,
  PERMIT2_ABI,
  sortCurrencies,
} from "../config/v4Contracts";
import { type TokenInfo, NATIVE_JASMY } from "../config/tokens";
import { getDeadline, calculateSlippage, parseTokenAmount } from "../lib/utils";
import { useTokenBalance } from "./useTokenBalance";
import { useTokenAllowance } from "./useTokenAllowance";
import { useV4Quote, type BestPool } from "./useV4Quote";

export interface SwapState {
  tokenIn: TokenInfo;
  tokenOut: TokenInfo;
  amountIn: string;
  amountOut: string;
  slippageBps: number;
}

export interface SwapResult {
  state: SwapState;
  balanceIn: bigint;
  balanceOut: bigint;
  quotedAmountOut: bigint;
  isQuoting: boolean;
  needsApproval: boolean;
  approve: () => void;
  isApproving: boolean;
  executeSwap: () => void;
  isSwapping: boolean;
  isConfirming: boolean;
  isConfirmed: boolean;
  txHash: `0x${string}` | undefined;
  flipTokens: () => void;
  setTokenIn: (token: TokenInfo) => void;
  setTokenOut: (token: TokenInfo) => void;
  setAmountIn: (amount: string) => void;
  setSlippage: (bps: number) => void;
  insufficientBalance: boolean;
  priceImpact: number;
  path: Address[];
  routeType: "V2" | "CL" | "BIN";
  bestPool: BestPool | null;
  v4BetterBy: bigint;
}

// Permit2 expiration: 30 days from now (uint48)
function permit2Expiration(): number {
  return Math.floor(Date.now() / 1000) + 60 * 60 * 24 * 30;
}

export function useSwap(): SwapResult {
  const { address: account } = useAccount();

  const [state, setState] = useState<SwapState>({
    tokenIn: NATIVE_JASMY,
    tokenOut: {
      address: ADDRESSES.WJASMY,
      symbol: "WJASMY",
      name: "Wrapped JASMY",
      decimals: 18,
      logoUrl: "/jasmy.svg",
    },
    amountIn: "",
    amountOut: "",
    slippageBps: 50,
  });

  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();
  const [permit2ApproveTxHash, setPermit2ApproveTxHash] = useState<
    `0x${string}` | undefined
  >();

  const { balance: balanceIn, refetch: refetchBalanceIn } = useTokenBalance(
    state.tokenIn.isNative ? undefined : state.tokenIn.address
  );
  const { balance: balanceOut, refetch: refetchBalanceOut } = useTokenBalance(
    state.tokenOut.isNative ? undefined : state.tokenOut.address
  );

  const parsedAmountIn = parseTokenAmount(state.amountIn, state.tokenIn.decimals);

  const tokenInForRouter: Address = state.tokenIn.isNative
    ? ADDRESSES.WJASMY
    : state.tokenIn.address;
  const tokenOutForRouter: Address = state.tokenOut.isNative
    ? ADDRESSES.WJASMY
    : state.tokenOut.address;

  const v2Path: Address[] =
    tokenInForRouter === tokenOutForRouter
      ? [tokenInForRouter]
      : [tokenInForRouter, tokenOutForRouter];

  // ── V2 Quote ───────────────────────────────────────────────────────────────
  const { data: amountsOut, isLoading: isV2Quoting } = useReadContract({
    address: ADDRESSES.V2_ROUTER,
    abi: V2_ROUTER_ABI,
    functionName: "getAmountsOut",
    args: [parsedAmountIn, v2Path],
    query: {
      enabled: parsedAmountIn > 0n && v2Path.length >= 2,
      refetchInterval: 10_000,
    },
  });

  const v2QuotedAmountOut =
    amountsOut && Array.isArray(amountsOut) && amountsOut.length > 1
      ? (amountsOut[amountsOut.length - 1] as bigint)
      : 0n;

  // ── V4 Quote ───────────────────────────────────────────────────────────────
  const {
    bestQuote: v4QuotedAmountOut,
    bestPool,
    isLoading: isV4Quoting,
  } = useV4Quote(state.tokenIn, state.tokenOut, parsedAmountIn);

  // ── Route selection ────────────────────────────────────────────────────────
  const useV4 = v4QuotedAmountOut > v2QuotedAmountOut && bestPool !== null;
  const quotedAmountOut = useV4 ? v4QuotedAmountOut : v2QuotedAmountOut;
  const routeType: "V2" | "CL" | "BIN" = useV4 ? (bestPool?.type ?? "V2") : "V2";
  const v4BetterBy = useV4 ? v4QuotedAmountOut - v2QuotedAmountOut : 0n;
  const isQuoting = isV2Quoting || isV4Quoting;

  // ── V2 price impact calculation ────────────────────────────────────────────
  const { data: pairAddress } = useReadContract({
    address: ADDRESSES.V2_FACTORY,
    abi: V2_FACTORY_ABI,
    functionName: "getPair",
    args: [tokenInForRouter, tokenOutForRouter],
    query: { enabled: v2Path.length >= 2, staleTime: 30_000 },
  });

  const pairAddr = pairAddress as Address | undefined;
  const hasPair =
    pairAddr && pairAddr !== "0x0000000000000000000000000000000000000000";

  const { data: reserves } = useReadContract({
    address: pairAddr ?? "0x0000000000000000000000000000000000000000",
    abi: V2_PAIR_ABI,
    functionName: "getReserves",
    query: { enabled: !!hasPair, refetchInterval: 10_000 },
  });

  const { data: token0Addr } = useReadContract({
    address: pairAddr ?? "0x0000000000000000000000000000000000000000",
    abi: V2_PAIR_ABI,
    functionName: "token0",
    query: { enabled: !!hasPair, staleTime: Infinity },
  });

  const priceImpact = useMemo(() => {
    if (
      !reserves ||
      !token0Addr ||
      parsedAmountIn === 0n ||
      quotedAmountOut === 0n ||
      useV4
    )
      return 0;
    const reserveData = reserves as [bigint, bigint, number];
    const isToken0In =
      (token0Addr as Address).toLowerCase() === tokenInForRouter.toLowerCase();
    const reserveIn = isToken0In ? reserveData[0] : reserveData[1];
    const reserveOut = isToken0In ? reserveData[1] : reserveData[0];
    if (reserveIn === 0n || reserveOut === 0n) return 0;
    const midPrice = Number(reserveOut) / Number(reserveIn);
    const execPrice = Number(quotedAmountOut) / Number(parsedAmountIn);
    return Math.max(0, (1 - execPrice / midPrice) * 100);
  }, [
    reserves,
    token0Addr,
    parsedAmountIn,
    quotedAmountOut,
    tokenInForRouter,
    useV4,
  ]);

  // ── Sync displayed output ──────────────────────────────────────────────────
  useEffect(() => {
    if (quotedAmountOut > 0n) {
      const formatted = formatUnits(quotedAmountOut, state.tokenOut.decimals);
      setState((prev) => ({ ...prev, amountOut: formatted }));
    } else if (parsedAmountIn === 0n) {
      setState((prev) => ({ ...prev, amountOut: "" }));
    }
  }, [quotedAmountOut, parsedAmountIn, state.tokenOut.decimals]);

  // ── V2 Approval (spender = V2_ROUTER) ─────────────────────────────────────
  const { needsApproval: needsV2Approval, approve: approveV2, isApproving: isApprovingV2 } =
    useTokenAllowance(
      state.tokenIn.isNative ? undefined : state.tokenIn.address,
      ADDRESSES.V2_ROUTER,
      routeType === "V2" ? parsedAmountIn : 0n
    );

  // ── Permit2 allowance check for V4 ────────────────────────────────────────
  const isNativeIn = state.tokenIn.isNative;

  const { data: permit2AllowanceData } = useReadContract({
    address: ADDRESSES.PERMIT2,
    abi: PERMIT2_ABI,
    functionName: "allowance",
    args: account && !isNativeIn
      ? [account, state.tokenIn.address as Address, ADDRESSES.SWAP_ROUTER]
      : undefined,
    query: {
      enabled: !!account && !isNativeIn && useV4,
      refetchInterval: 8_000,
    },
  });

  // ERC20 → Permit2 approval (Permit2 itself as spender on the token)
  const { needsApproval: needsErc20ToPermit2, approve: approveErc20ToPermit2, isApproving: isApprovingErc20 } =
    useTokenAllowance(
      state.tokenIn.isNative ? undefined : state.tokenIn.address,
      ADDRESSES.PERMIT2,
      !isNativeIn && useV4 ? parsedAmountIn : 0n
    );

  const permit2Amount =
    permit2AllowanceData ? (permit2AllowanceData as [bigint, number, number])[0] : 0n;
  const permit2Expiry =
    permit2AllowanceData ? (permit2AllowanceData as [bigint, number, number])[1] : 0;
  const nowSecs = Math.floor(Date.now() / 1000);
  const needsPermit2Approval =
    !isNativeIn &&
    useV4 &&
    !needsErc20ToPermit2 &&
    (permit2Amount < parsedAmountIn || permit2Expiry < nowSecs);

  const { writeContract: writePermit2, isPending: isApprovingPermit2 } =
    useWriteContract();

  const { isLoading: isWaitingPermit2, isSuccess: permit2Confirmed } =
    useWaitForTransactionReceipt({ hash: permit2ApproveTxHash });

  useEffect(() => {
    if (permit2Confirmed) setPermit2ApproveTxHash(undefined);
  }, [permit2Confirmed]);

  function approvePermit2() {
    if (!state.tokenIn.address || isNativeIn) return;
    writePermit2(
      {
        address: ADDRESSES.PERMIT2,
        abi: PERMIT2_ABI,
        functionName: "approve",
        args: [
          state.tokenIn.address as Address,
          ADDRESSES.SWAP_ROUTER,
          maxUint160,
          permit2Expiration(),
        ],
      },
      { onSuccess: (hash) => setPermit2ApproveTxHash(hash) }
    );
  }

  // ── Combined approval state ────────────────────────────────────────────────
  const needsApproval = routeType === "V2"
    ? needsV2Approval
    : needsErc20ToPermit2 || needsPermit2Approval;

  const isApproving =
    isApprovingV2 ||
    isApprovingErc20 ||
    isApprovingPermit2 ||
    isWaitingPermit2;

  function approve() {
    if (routeType === "V2") {
      approveV2();
    } else if (needsErc20ToPermit2) {
      approveErc20ToPermit2();
    } else if (needsPermit2Approval) {
      approvePermit2();
    }
  }

  // ── Write contract ─────────────────────────────────────────────────────────
  const { writeContract, isPending: isSwapping } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isConfirmed } =
    useWaitForTransactionReceipt({ hash: txHash });

  useEffect(() => {
    if (isConfirmed) {
      setState((prev) => ({ ...prev, amountIn: "", amountOut: "" }));
      refetchBalanceIn();
      refetchBalanceOut();
      setTxHash(undefined);
    }
  }, [isConfirmed, refetchBalanceIn, refetchBalanceOut]);

  // ── Execute swap ───────────────────────────────────────────────────────────
  const executeSwap = useCallback(() => {
    if (!account || parsedAmountIn === 0n || quotedAmountOut === 0n) return;

    const deadline = getDeadline(20);
    const minOut = calculateSlippage(quotedAmountOut, state.slippageBps);

    if (routeType !== "V2" && bestPool?.poolKey) {
      // ── V4 SmartRouter exactInputSingle ──────────────────────────────────
      const poolKey = bestPool.poolKey;
      const [c0] = sortCurrencies(tokenInForRouter, tokenOutForRouter);
      const zeroForOne = tokenInForRouter.toLowerCase() === c0.toLowerCase();
      const clampedIn =
        parsedAmountIn > 0xffffffffffffffffffffffffffffffffn
          ? 0xffffffffffffffffffffffffffffffffn
          : parsedAmountIn;
      const clampedMinOut =
        minOut > 0xffffffffffffffffffffffffffffffffn
          ? 0xffffffffffffffffffffffffffffffffn
          : minOut;

      writeContract(
        {
          address: ADDRESSES.SWAP_ROUTER,
          abi: SMART_ROUTER_ABI,
          functionName: "exactInputSingle",
          args: [
            {
              poolKey,
              zeroForOne,
              recipient: account,
              amountIn: clampedIn,
              amountOutMinimum: clampedMinOut,
              hookData: "0x",
            },
          ],
          value: state.tokenIn.isNative ? parsedAmountIn : 0n,
        },
        { onSuccess: (hash) => setTxHash(hash) }
      );
    } else if (state.tokenIn.isNative && !state.tokenOut.isNative) {
      // ── V2: JASMY → Token ─────────────────────────────────────────────────
      writeContract(
        {
          address: ADDRESSES.V2_ROUTER,
          abi: V2_ROUTER_ABI,
          functionName: "swapExactJASMYForTokens",
          args: [minOut, [ADDRESSES.WJASMY, state.tokenOut.address], account, deadline],
          value: parsedAmountIn,
        },
        { onSuccess: (hash) => setTxHash(hash) }
      );
    } else if (!state.tokenIn.isNative && state.tokenOut.isNative) {
      // ── V2: Token → JASMY ─────────────────────────────────────────────────
      writeContract(
        {
          address: ADDRESSES.V2_ROUTER,
          abi: V2_ROUTER_ABI,
          functionName: "swapExactTokensForJASMY",
          args: [
            parsedAmountIn,
            minOut,
            [state.tokenIn.address, ADDRESSES.WJASMY],
            account,
            deadline,
          ],
        },
        { onSuccess: (hash) => setTxHash(hash) }
      );
    } else {
      // ── V2: Token → Token ─────────────────────────────────────────────────
      writeContract(
        {
          address: ADDRESSES.V2_ROUTER,
          abi: V2_ROUTER_ABI,
          functionName: "swapExactTokensForTokens",
          args: [parsedAmountIn, minOut, v2Path, account, deadline],
        },
        { onSuccess: (hash) => setTxHash(hash) }
      );
    }
  }, [
    account,
    parsedAmountIn,
    quotedAmountOut,
    state,
    v2Path,
    routeType,
    bestPool,
    tokenInForRouter,
    tokenOutForRouter,
    writeContract,
  ]);

  // ── State setters ──────────────────────────────────────────────────────────
  const flipTokens = useCallback(() => {
    setState((prev) => ({
      ...prev,
      tokenIn: prev.tokenOut,
      tokenOut: prev.tokenIn,
      amountIn: prev.amountOut,
      amountOut: prev.amountIn,
    }));
  }, []);

  const setTokenIn = useCallback((token: TokenInfo) => {
    setState((prev) => {
      if (
        prev.tokenOut.address === token.address &&
        prev.tokenOut.isNative === token.isNative
      ) {
        return { ...prev, tokenIn: token, tokenOut: prev.tokenIn };
      }
      return { ...prev, tokenIn: token };
    });
  }, []);

  const setTokenOut = useCallback((token: TokenInfo) => {
    setState((prev) => {
      if (
        prev.tokenIn.address === token.address &&
        prev.tokenIn.isNative === token.isNative
      ) {
        return { ...prev, tokenOut: token, tokenIn: prev.tokenOut };
      }
      return { ...prev, tokenOut: token };
    });
  }, []);

  const setAmountIn = useCallback((amount: string) => {
    setState((prev) => ({ ...prev, amountIn: amount }));
  }, []);

  const setSlippage = useCallback((bps: number) => {
    setState((prev) => ({ ...prev, slippageBps: bps }));
  }, []);

  const insufficientBalance = parsedAmountIn > 0n && parsedAmountIn > balanceIn;

  return {
    state,
    balanceIn,
    balanceOut,
    quotedAmountOut,
    isQuoting,
    needsApproval,
    approve,
    isApproving,
    executeSwap,
    isSwapping,
    isConfirming,
    isConfirmed,
    txHash,
    flipTokens,
    setTokenIn,
    setTokenOut,
    setAmountIn,
    setSlippage,
    insufficientBalance,
    priceImpact,
    path: v2Path,
    routeType,
    bestPool,
    v4BetterBy,
  };
}
